import { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Trash2, ArrowUp, ArrowDown, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { DynamicIcon } from '@/utils/iconMap';
import IconPicker from '@/components/IconPicker';
import { API_URL } from '@/config/api';

const newCourse = () => ({
  id: `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  title: '', subtitle: '', badge: '', is_featured: false, features: [], sort_order: 0,
});

const newSectionFeature = () => ({
  id: `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  icon: 'Star', title: '', description: '',
});

const AdminCoursesContent = () => {
  const [content, setContent] = useState({ hero_title: '', hero_subtitle: '', courses: [], section_features: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedCourses, setExpandedCourses] = useState(new Set());
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [deleteCourseIdx, setDeleteCourseIdx] = useState(null);
  const [deleteSectionIdx, setDeleteSectionIdx] = useState(null);

  useEffect(() => { fetchContent(); }, []);

  const fetchContent = async () => {
    try {
      const res = await axios.get(`${API_URL}/courses-content`);
      setContent({
        hero_title: res.data.hero_title || '',
        hero_subtitle: res.data.hero_subtitle || '',
        courses: res.data.courses || [],
        section_features: res.data.section_features || [],
      });
    } catch { toast.error('載入失敗'); }
    finally { setLoading(false); }
  };

  const toggleCourse = (id) => {
    setExpandedCourses(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSection = (id) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const updateField = (field, value) => setContent({ ...content, [field]: value });

  const updateCourse = (index, field, value) => {
    const updated = [...content.courses];
    updated[index] = { ...updated[index], [field]: value };
    setContent({ ...content, courses: updated });
  };

  const addCourse = () => {
    const course = newCourse();
    setContent({ ...content, courses: [...content.courses, course] });
    setExpandedCourses(prev => new Set([...prev, course.id]));
  };

  const confirmRemoveCourse = () => {
    if (deleteCourseIdx === null) return;
    setContent({ ...content, courses: content.courses.filter((_, i) => i !== deleteCourseIdx) });
    setDeleteCourseIdx(null);
  };

  const moveCourse = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= content.courses.length) return;
    const reordered = [...content.courses];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setContent({ ...content, courses: reordered });
  };

  const addCourseFeature = (courseIndex) => {
    const updated = [...content.courses];
    updated[courseIndex] = { ...updated[courseIndex], features: [...(updated[courseIndex].features || []), ''] };
    setContent({ ...content, courses: updated });
  };

  const updateCourseFeature = (courseIndex, featureIndex, value) => {
    const updated = [...content.courses];
    const features = [...(updated[courseIndex].features || [])];
    features[featureIndex] = value;
    updated[courseIndex] = { ...updated[courseIndex], features };
    setContent({ ...content, courses: updated });
  };

  const removeCourseFeature = (courseIndex, featureIndex) => {
    const updated = [...content.courses];
    updated[courseIndex] = { ...updated[courseIndex], features: (updated[courseIndex].features || []).filter((_, i) => i !== featureIndex) };
    setContent({ ...content, courses: updated });
  };

  const updateSectionFeature = (index, field, value) => {
    const updated = [...content.section_features];
    updated[index] = { ...updated[index], [field]: value };
    setContent({ ...content, section_features: updated });
  };

  const addSectionFeature = () => {
    const feature = newSectionFeature();
    setContent({ ...content, section_features: [...content.section_features, feature] });
    setExpandedSections(prev => new Set([...prev, feature.id]));
  };

  const confirmRemoveSectionFeature = () => {
    if (deleteSectionIdx === null) return;
    setContent({ ...content, section_features: content.section_features.filter((_, i) => i !== deleteSectionIdx) });
    setDeleteSectionIdx(null);
  };

  const moveSectionFeature = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= content.section_features.length) return;
    const reordered = [...content.section_features];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setContent({ ...content, section_features: reordered });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        id: 'courses',
        hero_title: content.hero_title,
        hero_subtitle: content.hero_subtitle,
        courses: content.courses.map((c, i) => ({
          id: c.id && !c.id.startsWith('temp-') ? c.id : undefined,
          title: c.title, subtitle: c.subtitle || '', badge: c.badge || '',
          is_featured: !!c.is_featured,
          features: (c.features || []).filter(f => f && f.trim() !== ''),
          sort_order: i,
        })),
        section_features: content.section_features.map(f => ({
          id: f.id && !f.id.startsWith('temp-') ? f.id : undefined,
          icon: f.icon, title: f.title, description: f.description,
        })),
      };
      const res = await axios.put(`${API_URL}/courses-content`, payload);
      setContent({
        hero_title: res.data.hero_title || '',
        hero_subtitle: res.data.hero_subtitle || '',
        courses: res.data.courses || [],
        section_features: res.data.section_features || [],
      });
      toast.success('儲存成功');
    } catch { toast.error('儲存失敗'); }
    finally { setSaving(false); }
  };

  if (loading) return <AdminLayout><div className="text-center py-12">載入中...</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold">課程內容管理</h1>
            <p className="text-muted-foreground mt-1">管理課程資訊頁面的標題、課程列表和特色項目</p>
          </div>
          <Button onClick={handleSave} disabled={saving} size="lg">{saving ? '儲存中...' : '儲存全部'}</Button>
        </div>

        {/* Hero */}
        <Card>
          <CardHeader><CardTitle>頂部橫幅</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>主標題</Label>
              <Input value={content.hero_title} onChange={e => updateField('hero_title', e.target.value)} placeholder="例如：課程資訊" />
            </div>
            <div>
              <Label>副標題</Label>
              <Input value={content.hero_subtitle} onChange={e => updateField('hero_subtitle', e.target.value)} placeholder="例如：選擇最適合您的課程，開啟學車之旅" />
            </div>
          </CardContent>
        </Card>

        {/* Courses — toggle list */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>課程列表</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">點擊課程名稱展開編輯</p>
            </div>
            <Button variant="outline" size="sm" onClick={addCourse}>
              <Plus className="mr-2 h-4 w-4" /> 新增課程
            </Button>
          </CardHeader>
          <CardContent>
            {content.courses.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">尚未有課程</p>
            ) : (
              <div className="space-y-2">
                {content.courses.map((course, index) => {
                  const isExpanded = expandedCourses.has(course.id);
                  return (
                    <div key={course.id} className="border rounded-lg bg-gray-50 overflow-hidden">
                      {/* Header row */}
                      <div
                        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-100 transition-colors select-none"
                        onClick={() => toggleCourse(course.id)}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <ChevronDown className={`h-4 w-4 flex-shrink-0 text-gray-500 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                          <span className="font-medium truncate">
                            {course.title || <span className="text-muted-foreground italic">課程 {index + 1}（未命名）</span>}
                          </span>
                          {course.subtitle && <span className="text-xs text-muted-foreground hidden sm:inline">— {course.subtitle}</span>}
                          {course.badge && (
                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full flex-shrink-0">{course.badge}</span>
                          )}
                          {course.is_featured && (
                            <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full flex-shrink-0">精選</span>
                          )}
                        </div>
                        <div className="flex gap-1 flex-shrink-0 ml-2" onClick={e => e.stopPropagation()}>
                          <Button size="sm" variant="outline" onClick={() => moveCourse(index, -1)} disabled={index === 0}>
                            <ArrowUp className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => moveCourse(index, 1)} disabled={index === content.courses.length - 1}>
                            <ArrowDown className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => setDeleteCourseIdx(index)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Expanded edit area */}
                      {isExpanded && (
                        <div className="px-4 pb-4 pt-3 border-t bg-white">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                            <div>
                              <Label>標題</Label>
                              <Input value={course.title} onChange={e => updateCourse(index, 'title', e.target.value)} placeholder="例如：私家車課程" />
                            </div>
                            <div>
                              <Label>副標題</Label>
                              <Input value={course.subtitle || ''} onChange={e => updateCourse(index, 'subtitle', e.target.value)} placeholder="例如：棍波/自動波" />
                            </div>
                            <div>
                              <Label>徽章文字</Label>
                              <Input value={course.badge || ''} onChange={e => updateCourse(index, 'badge', e.target.value)} placeholder="例如：最受歡迎（可空）" />
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mb-3">
                            <input type="checkbox" id={`featured-${course.id}`} checked={!!course.is_featured}
                              onChange={e => updateCourse(index, 'is_featured', e.target.checked)} className="w-4 h-4" />
                            <Label htmlFor={`featured-${course.id}`}>標示為精選（卡片會有特殊邊框）</Label>
                          </div>
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <Label>特色列表</Label>
                              <Button size="sm" variant="outline" onClick={() => addCourseFeature(index)}>
                                <Plus className="h-3 w-3 mr-1" /> 加項目
                              </Button>
                            </div>
                            {(course.features || []).length === 0 ? (
                              <p className="text-sm text-muted-foreground bg-gray-50 rounded p-3 text-center">未有特色項目</p>
                            ) : (
                              <div className="space-y-2">
                                {(course.features || []).map((feat, fi) => (
                                  <div key={fi} className="flex gap-2">
                                    <Input value={feat} onChange={e => updateCourseFeature(index, fi, e.target.value)} placeholder="例如：一對一專屬教學" />
                                    <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => removeCourseFeature(index, fi)}>
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section features — toggle list */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>「課程特色」項目</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">點擊項目標題展開編輯</p>
            </div>
            <Button variant="outline" size="sm" onClick={addSectionFeature}>
              <Plus className="mr-2 h-4 w-4" /> 新增項目
            </Button>
          </CardHeader>
          <CardContent>
            {content.section_features.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">尚未有特色項目</p>
            ) : (
              <div className="space-y-2">
                {content.section_features.map((feature, index) => {
                  const isExpanded = expandedSections.has(feature.id);
                  return (
                    <div key={feature.id} className="border rounded-lg bg-gray-50 overflow-hidden">
                      {/* Header row */}
                      <div
                        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-100 transition-colors select-none"
                        onClick={() => toggleSection(feature.id)}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <ChevronDown className={`h-4 w-4 flex-shrink-0 text-gray-500 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                          <div className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                            <DynamicIcon name={feature.icon} className="h-3.5 w-3.5 text-blue-600" />
                          </div>
                          <span className="font-medium truncate">
                            {feature.title || <span className="text-muted-foreground italic">項目 {index + 1}（未命名）</span>}
                          </span>
                        </div>
                        <div className="flex gap-1 flex-shrink-0 ml-2" onClick={e => e.stopPropagation()}>
                          <Button size="sm" variant="outline" onClick={() => moveSectionFeature(index, -1)} disabled={index === 0}><ArrowUp className="h-4 w-4" /></Button>
                          <Button size="sm" variant="outline" onClick={() => moveSectionFeature(index, 1)} disabled={index === content.section_features.length - 1}><ArrowDown className="h-4 w-4" /></Button>
                          <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => setDeleteSectionIdx(index)}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </div>
                      {/* Expanded edit area */}
                      {isExpanded && (
                        <div className="px-4 pb-4 pt-3 border-t bg-white">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                            <div>
                              <IconPicker label="圖示" value={feature.icon} onChange={v => updateSectionFeature(index, 'icon', v)} />
                            </div>
                            <div>
                              <Label>標題</Label>
                              <Input value={feature.title} onChange={e => updateSectionFeature(index, 'title', e.target.value)} className="mt-1" />
                            </div>
                          </div>
                          <div>
                            <Label>描述</Label>
                            <Textarea value={feature.description} onChange={e => updateSectionFeature(index, 'description', e.target.value)} rows={2} className="mt-1" />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} size="lg">{saving ? '儲存中...' : '儲存全部'}</Button>
        </div>
      </div>

      {/* Delete course confirm */}
      <AlertDialog open={deleteCourseIdx !== null} onOpenChange={o => !o && setDeleteCourseIdx(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確定要刪除此課程？</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteCourseIdx !== null && content.courses[deleteCourseIdx]?.title
                ? `「${content.courses[deleteCourseIdx].title}」將被移除。`
                : '此課程將被移除。'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemoveCourse} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">確定刪除</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete section feature confirm */}
      <AlertDialog open={deleteSectionIdx !== null} onOpenChange={o => !o && setDeleteSectionIdx(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確定要刪除此特色項目？</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteSectionIdx !== null && content.section_features[deleteSectionIdx]?.title
                ? `「${content.section_features[deleteSectionIdx].title}」將被移除。`
                : '此項目將被移除。'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemoveSectionFeature} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">確定刪除</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default AdminCoursesContent;
